// Spec §68: full workspace — search + status filter + date range +
// list, with the detail pane on the right (desktop split view).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import {
  REPORT_STATUS_LABEL,
  REPORT_STATUS_TONE,
  formatDate,
  formatRelative,
} from '@/utils/format';
import { useIsMobile } from '@/hooks/useSpeechRecognition';
import type { ReportJson, ReportStatus } from '@/api/types';

type StatusFilter = 'all' | ReportStatus;
type DatePreset = 'last7' | 'last30' | 'lastYear' | 'custom';

const STATUS_FILTERS: StatusFilter[] = ['all', 'draft', 'submitted', 'archived'];

const DATE_PRESETS: { label: string; value: DatePreset }[] = [
  { label: 'Last 7 Days', value: 'last7' },
  { label: 'Last 30 Days', value: 'last30' },
  { label: 'Last Year', value: 'lastYear' },
  { label: 'Custom', value: 'custom' },
];

function presetRange(preset: DatePreset): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10); // YYYY-MM-DD today
  let from: Date;
  switch (preset) {
    case 'last7':
      from = new Date(now);
      from.setDate(now.getDate() - 7);
      break;
    case 'last30':
      from = new Date(now);
      from.setDate(now.getDate() - 30);
      break;
    case 'lastYear':
      from = new Date(now);
      from.setFullYear(now.getFullYear() - 1);
      break;
    default:
      return { from: '', to: '' };
  }
  return { from: from.toISOString().slice(0, 10), to };
}

function isManualRange(from: string, to: string): boolean {
  return !!(from && to);
}

export function WorkspaceScreen() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<ReportJson[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Responsive: true on viewports below 880px (split-view disabled).
  const isMobile = useIsMobile();

  // Local selectedId survives Detail→Workspace navigation on Desktop so the
  // split-view highlight can be restored after re-fetching the list.
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(() =>
    searchParams.get('id'),
  );

  const filter = (searchParams.get('status') as StatusFilter) || 'all';
  const q = searchParams.get('q') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const urlSelectedId = searchParams.get('id') ?? null;

  // Sync local selection with URL when the URL changes from outside the component.
  useEffect(() => {
    setLocalSelectedId(urlSelectedId);
  }, [urlSelectedId]);

  // Adopt local selection if the report still exists; otherwise fall back to URL.
  const effectiveSelectedId = useMemo(() => {
    if (localSelectedId && rows.find((r) => r.id === localSelectedId)) {
      return localSelectedId;
    }
    return urlSelectedId;
  }, [localSelectedId, rows, urlSelectedId]);

  // Prune stale local selection when the list changes (e.g. after delete).
  useEffect(() => {
    if (localSelectedId && !rows.find((r) => r.id === localSelectedId)) {
      setLocalSelectedId(null);
    }
  }, [rows, localSelectedId]);

  // Derive the active date preset from the URL params.
  const datePreset: DatePreset = useMemo(() => {
    if (!from && !to) return 'last7'; // default
    if (from && to) {
      const range = presetRange('last7');
      if (from === range.from && to === range.to) return 'last7';
      const range30 = presetRange('last30');
      if (from === range30.from && to === range30.to) return 'last30';
      const rangeYear = presetRange('lastYear');
      if (from === rangeYear.from && to === rangeYear.to) return 'lastYear';
    }
    return 'custom';
  }, [from, to]);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectReport = useCallback(
    (id: string | null) => {
      setLocalSelectedId(id);
      if (id) {
        setParam('id', id);
      } else {
        setParam('id', null);
      }
    },
    [setParam],
  );

  const selectDatePreset = useCallback(
    (preset: DatePreset) => {
      if (preset === 'custom') {
        setParam('from', null);
        setParam('to', null);
      } else {
        const { from: f, to: t } = presetRange(preset);
        setParam('from', f);
        setParam('to', t);
      }
    },
    [setParam],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBusy(true);
      setError(null);
      try {
        const since = from ? new Date(`${from}T00:00:00Z`).toISOString() : undefined;
        const { rows: data } = await reportsApi.list(getToken, {
          status: filter === 'all' ? undefined : filter,
          since,
        });
        let filtered = data;
        if (q.trim().length > 0) {
          const needle = q.trim().toLowerCase();
          filtered = filtered.filter(
            (r) =>
              r.title.toLowerCase().includes(needle) || r.content.toLowerCase().includes(needle),
          );
        }
        if (to) {
          const toDate = new Date(`${to}T23:59:59Z`).getTime();
          filtered = filtered.filter((r) => new Date(r.updated_at).getTime() <= toDate);
        }
        filtered.sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
        if (!cancelled) setRows(filtered);
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load reports.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filter, q, from, to, getToken]);

  const selected = useMemo(
    () => (effectiveSelectedId ? (rows.find((r) => r.id === effectiveSelectedId) ?? null) : null),
    [rows, effectiveSelectedId],
  );

  return (
    <div className="stack">
      <div className="card workspace-filters">
        {/* Row 1: Search + New Report */}
        <div className="row workspace-filter-row">
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="q" className="workspace-search-label">
              Search
            </label>
            <input
              id="q"
              className="input"
              type="search"
              placeholder="Search title or content"
              defaultValue={q}
              onChange={(e) => setParam('q', e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              className="btn btn--primary workspace-new-report-btn"
              type="button"
              onClick={() => navigate('/reports/new')}
            >
              New report
            </button>
          </div>
        </div>

        {/* Row 2: Date presets */}
        <div className="workspace-date-presets" role="group" aria-label="Date range">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`btn workspace-preset-btn${datePreset === p.value ? ' btn--primary' : ' btn--ghost'}`}
              onClick={() => selectDatePreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Row 3: Custom date inputs — shown only when Custom is selected */}
        {datePreset === 'custom' || isManualRange(from, to) ? (
          <div className="row workspace-date-inputs">
            <div className="field">
              <label htmlFor="from">From</label>
              <input
                id="from"
                className="input"
                type="date"
                defaultValue={from}
                onChange={(e) => setParam('from', e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="to">To</label>
              <input
                id="to"
                className="input"
                type="date"
                defaultValue={to}
                onChange={(e) => setParam('to', e.target.value)}
              />
            </div>
          </div>
        ) : null}

        {/* Row 4: Status filters */}
        <div className="workspace-status-filters" role="group" aria-label="Report status">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={`btn${filter === f ? ' btn--primary' : ' btn--ghost'}`}
              onClick={() => setParam('status', f === 'all' ? null : f)}
            >
              {f === 'all' ? 'All' : REPORT_STATUS_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="workspace-split">
        <div className="card" style={{ padding: 12 }}>
          {error ? <div className="error-banner">{error}</div> : null}
          {busy ? (
            <div className="empty">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="empty">
              {q || from || to || filter !== 'all'
                ? 'No reports match the current filters.'
                : 'No reports yet.'}
            </div>
          ) : (
            <div className="list">
              {rows.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className="list__item"
                  onClick={() => {
                    if (isMobile) {
                      // Mobile: navigate directly to the detail page.
                      navigate(`/reports/${row.id}`);
                    } else {
                      // Desktop: select for the split-view preview.
                      selectReport(row.id);
                    }
                  }}
                  style={{
                    background:
                      row.id === effectiveSelectedId ? 'var(--color-bg-muted)' : undefined,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="list__item-title">
                      {row.title.trim() === '' ? '(Untitled)' : row.title}
                    </div>
                    <div className="list__item-subtitle">
                      <span className={REPORT_STATUS_TONE[row.status]}>
                        {REPORT_STATUS_LABEL[row.status]}
                      </span>
                      <span>· {formatRelative(row.updated_at)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="card">
          {selected ? (
            <div className="stack">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>
                  {selected.title.trim() === '' ? '(Untitled)' : selected.title}
                </h2>
                <span className={REPORT_STATUS_TONE[selected.status]}>
                  {REPORT_STATUS_LABEL[selected.status]}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                Updated {formatDate(selected.updated_at)} · v{selected.version}
              </div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{selected.content || '(No content yet.)'}</p>
              <div className="row">
                {/*
                  Mobile lifecycle rule (commit 6343a80): Edit is only
                  available on Draft reports. View Details and Photos
                  remain visible for every non-deleted report — Photos
                  is a read-only viewer on the Web, so it is safe for
                  Submitted / Archived as well.
                */}
                {selected.status === 'draft' ? (
                  <button
                    className="btn btn--primary"
                    onClick={() => navigate(`/reports/${selected.id}/edit`)}
                  >
                    Edit
                  </button>
                ) : null}
                <button className="btn" onClick={() => navigate(`/reports/${selected.id}`)}>
                  View Details
                </button>
                <button className="btn" onClick={() => navigate(`/reports/${selected.id}/photos`)}>
                  Photos
                </button>
              </div>
            </div>
          ) : (
            <div className="empty">Select a report from the list to preview it here.</div>
          )}
        </div>
      </div>
    </div>
  );
}
